import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod/v3'
import { Save } from 'lucide-react'

import type { LotOption } from '@/components/lots/lot-items-field'
import type { LotPickerSource } from '@/components/lots/lot-picker-source'
import type { CreateOutNoteRequest } from '@/lib/api/scenting-lot-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { LotItemsField } from '@/components/lots/lot-items-field'
import { scentingLotApi } from '@/lib/api/scenting-lot-api'
import {
    ScanLotCodeButton,
    makeLotIdParser,
} from '@/components/lots/scan-lot-code-button'
import { useCreateScentingOutNote } from '@/hooks/use-scenting-lot-query'
import { scentingLotOption } from '@/components/lots/lot-option-builders'
import { useScentingLotSource } from '@/components/lots/lot-picker-source'
import { m } from '@/lib/paraglide/messages'

const itemSchema = z.object({
    kind: z.literal('existing'),
    // LotItemsField dùng tên generic 'sorting_lot_id'; ở scenting = scenting_lot_id.
    sorting_lot_id: z
        .string({ required_error: m.form_err_pick_existing_lot() })
        .uuid(m.form_err_pick_existing_lot()),
    quantity: z.coerce
        .number({
            required_error: m.form_err_quantity_required(),
            invalid_type_error: m.form_err_number_invalid(),
        })
        .positive(m.form_err_quantity_positive()),
})

const schema = z.object({
    reason: z.enum(['for_packaging', 'discard', 'adjustment']),
    notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
    items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
})

type FormData = z.infer<typeof schema>

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/scenting-warehouse/stock-out-notes/new',
)({
    component: NewPXKPage,
})

function useLotOptionsAndCache() {
    // Chỉ lô còn tồn — tìm server + cuộn tải thêm (nguồn lô dùng chung).
    const lotSource = useScentingLotSource({
        filters: { only_available: '1' },
    })
    const [scannedLots, setScannedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await scentingLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, scentingLotOption(l))
                return next
            })
        } catch {
            // ignore
        }
    }
    return { lotSource, scannedLots, fetchAndCacheLot }
}

function NewPXKPage() {
    const navigate = useNavigate()
    const create = useCreateScentingOutNote()
    const { lotSource, scannedLots, fetchAndCacheLot } = useLotOptionsAndCache()

    const handleSubmit = async (
        value: CreateOutNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => {
        try {
            const res = await workflow.run(
                () => create.mutateAsync(value),
                (result) => result.data?.id,
            )
            const noteId = res.data?.id
            if (noteId) {
                void navigate({
                    to: '/scenting-warehouse/stock-out-notes/$id',
                    params: { id: noteId },
                })
            }
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <ScentingLotStockOutForm
            lotSource={lotSource}
            scannedLots={Array.from(scannedLots.values())}
            onLotScanned={(id) => void fetchAndCacheLot(id)}
            isPending={create.isPending}
            onSubmit={handleSubmit}
            onCancel={() =>
                navigate({
                    to: '/scenting-warehouse/stock-out-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

export function ScentingLotStockOutForm({
    mode = 'create',
    initialValues,
    title,
    submitLabel,
    lotSource,
    scannedLots,
    onLotScanned,
    isPending,
    onSubmit,
    onCancel,
}: {
    mode?: 'create' | 'edit'
    initialValues?: FormData
    title?: string
    submitLabel?: string
    /** Nguồn lô lấy hương dùng chung (tìm server + cuộn tải thêm). */
    lotSource: LotPickerSource
    scannedLots?: LotOption[]
    onLotScanned?: (lotId: string) => void
    isPending: boolean
    onSubmit: (
        value: CreateOutNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => void
    onCancel: () => void
}) {
    const workflow = useWorkflowTracking('scenting_stock_out', {
        operation: mode === 'edit' ? 'update' : 'create',
    })
    const isEdit = mode === 'edit'
    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: initialValues ?? {
            reason: 'discard',
            notes: '',
            items: [{ kind: 'existing', sorting_lot_id: '', quantity: 0 }],
        },
    })

    useEffect(() => {
        const subscription = form.watch((_values, { name }) => {
            if (!name) return
            workflow.interact(name.startsWith('items') ? 'items' : 'details')
            if (name.endsWith('_lot_id') || name === 'reason')
                workflow.step('selection_changed')
        })
        return () => subscription.unsubscribe()
    }, [form, workflow])

    const submit = form.handleSubmit(
        (data) =>
            onSubmit(
                {
                    reason: data.reason,
                    notes: data.notes || undefined,
                    items: data.items.map((it) => ({
                        scenting_lot_id: it.sorting_lot_id,
                        quantity: Number(it.quantity),
                    })),
                },
                workflow,
            ),
        () => workflow.step('validation_failed'),
    )

    return (
        <div className="space-y-4 page-pad pb-24 md:pb-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <PageBackButton onBack={onCancel} />
                    <h1 className="text-xl font-semibold">
                        {title ?? m.scl_new_pxk_title()}
                    </h1>
                </div>
            </div>

            <FormProvider {...form}>
                <form
                    onSubmit={submit}
                    className="space-y-3 md:space-y-6 max-w-4xl"
                >
                    <div className="grid gap-4 md:grid-cols-2 rounded-md border bg-card p-4">
                        <div>
                            <Label className="mb-1 block">
                                {m.sl_new_reason_out()}
                            </Label>
                            <Select
                                value={form.watch('reason')}
                                disabled={isEdit}
                                onValueChange={(v) =>
                                    form.setValue(
                                        'reason',
                                        v as
                                            | 'for_packaging'
                                            | 'discard'
                                            | 'adjustment',
                                    )
                                }
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {/* Xuất đi đóng gói đã chuyển sang khai lô nguồn
                                        trên PNK kho thành phẩm — chỉ hiện khi xem/sửa
                                        phiếu cũ còn mang lý do này. */}
                                    {form.watch('reason') ===
                                        'for_packaging' && (
                                        <SelectItem value="for_packaging">
                                            {m.scl_out_reason_for_packaging()}
                                        </SelectItem>
                                    )}
                                    <SelectItem value="discard">
                                        {m.scl_out_reason_discard()}
                                    </SelectItem>
                                    <SelectItem value="adjustment">
                                        {m.sl_new_reason_out_adjustment()}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-1 block">
                                {m.sl_new_notes()}
                            </Label>
                            <Input
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                            />
                        </div>
                    </div>

                    <LotItemsField
                        mode="out"
                        lotSource={lotSource}
                        scannedLots={scannedLots}
                        categoryOptions={[]}
                        categoryLabel={m.sl_lot_grade()}
                        headerAction={
                            <ScanLotCodeButton
                                parseLotId={makeLotIdParser(
                                    'scenting-warehouse/lots',
                                )}
                                onScanned={(lotId) => {
                                    const current =
                                        form.getValues('items') ?? []
                                    form.setValue(
                                        'items',
                                        [
                                            ...current,
                                            {
                                                kind: 'existing',
                                                sorting_lot_id: lotId,
                                                quantity: 0,
                                            },
                                        ],
                                        {
                                            shouldDirty: true,
                                            shouldValidate: false,
                                        },
                                    )
                                    onLotScanned?.(lotId)
                                }}
                            />
                        }
                    />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {submitLabel ??
                                (isPending
                                    ? m.sl_new_submitting()
                                    : m.sl_new_submit_pnk())}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
